import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSpaceStore } from "../../../stores/Assets/space";
import { useConfirmDialogStore } from "../../../stores/Modal/ConfirmDialogStore";
import { t } from "i18next";
import DataTableBase from "../../../Components/Common/DataTableBase/DataTableBase";
import AddSubSpaces from "./components/AddSubSpaces";
import EditSubSpace from "./components/EditSubSpace";
import QRmodal from "../Spaces/Components/QRmodal";
import { canEdit } from "../../../helpers/permissions";
import { axiosHelper, tokenHelper } from "../../../stores/helpers";
import { QRCode } from "react-qrcode-logo";
import { Button, Spinner } from "reactstrap";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { convertQRToBlob } from "../../../utils/qrDownload";

const SubSpaces = () => {
  const { spaceid } = useParams();
  const [selectedRow, setSelectedRow] = useState(null);
  const {
    getSubSpaces,
    getSpace,
    space,
    deleteSubSpace,
    subspaces,
    isLoading,
    isLoadingSpace,
    isLoadingSubspaces,
    isError,
    error,
    pagination, // add pagination to the store
  } = useSpaceStore((state) => state);
  const [kitchenID, setKitchenID] = useState(null);
  const [menuIDs, setMenuIDs] = useState([]);
  const [showQRCode, setShowQRCode] = useState(false);
  const [selectedRowQR, setselectedRowQR] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { showConfirm } = useConfirmDialogStore((state) => state);
  const [showAddSubSpaceModal, setShowAddSubSpaceModal] = useState(false);
  const [showEditSubSpaceModal, setshowEditSubSpaceModal] = useState(false);
  const [isDownloadingQR, setIsDownloadingQR] = useState(false);
  const [allDownloadSubSpaces, setAllDownloadSubSpaces] = useState([]);
  const qrRefs = useRef({});

  useEffect(() => {
    if (spaceid) {
      const loadData = async () => {
        try {
          // Load space info first
          await getSpace(spaceid);
          // Then load subspaces
          await fetchSubSpaces(currentPage, rowsPerPage);
        } catch (error) {
          console.error("Error loading data:", error);
        }
      };
      loadData();
    }
  }, [spaceid]);

  useEffect(() => {
    if (space) {
      setKitchenID(space?.kitchen_id);
      const menu_ids = space?.menus?.map((menu) => menu?.menu_id);
      setMenuIDs(menu_ids);
    }
  }, [space]);

  useEffect(() => {
    // Only show modal if we're not loading and have confirmed there are no subspaces
    // Also ensure we have space info loaded
    if (
      !isLoadingSubspaces &&
      !isLoadingSpace &&
      !isError &&
      space &&
      subspaces.length === 0
    ) {
      setShowAddSubSpaceModal(true);
    } else {
      setShowAddSubSpaceModal(false);
    }
  }, [subspaces, isLoadingSubspaces, isLoadingSpace, isError, space]);

  const fetchSubSpaces = (page, limit) => {
    getSubSpaces(spaceid, page, limit);
  };

  const onChangePage = (page) => {
    setCurrentPage(page);
    fetchSubSpaces(page, rowsPerPage);
  };

  const onChangeRowsPerPage = (newPerPage, page) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(page);
    fetchSubSpaces(page, newPerPage);
  };

  const columns = [
    {
      name: t("Subspace-ID"),
      selector: (row) => row?.sub_space_id,
      sortable: true,
      wrap: true,
    },
    {
      name: t("Space-ID"),
      selector: (row) => row?.space_id,
      sortable: true,
      wrap: true,
    },
    {
      name: t("Name"),
      selector: (row) => row?.name,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.name}</span>
        </div>
      ),
    },
    {
      name: t("QR Code"),
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="cursor-pointer">
          <span
            style={{ fontSize: "14px" }}
            className={`badge bg-soft-success cursor-pointer text-success text-uppercase`}
            onClick={() => handleQRClick(row)}
          >
            {<i className="ri-external-link-line"></i>} View
          </span>
        </div>
      ),
    },
  ];

  const toggleAddSubSpaceModal = () => {
    setShowAddSubSpaceModal(!showAddSubSpaceModal);
  };

  const deleteSubSpaceFun = async (spaceid, id) => {
    try {
      await deleteSubSpace(spaceid, id);
      fetchSubSpaces(currentPage, rowsPerPage);
    } catch (e) {
      console.log(e);
    }
  };

  const toggleEditSubSpace = (row) => {
    setSelectedRow(row);
    setshowEditSubSpaceModal(!showEditSubSpaceModal);
  };

  const handleQRClick = (row) => {
    setselectedRowQR(row);
    setShowQRCode(!showQRCode);
  };

  const sanitizeFileName = (name) => String(name || "").replace(/[\\/]/g, "-");

  const fetchAllSubSpacesForDownload = async () => {
    const accessToken = await tokenHelper.getToken();
    const totalItems = pagination?.totalItems || 1000;

    const [subSpaceRes, qrRes] = await Promise.all([
      axiosHelper.get(
        `/sub-space/space/${spaceid}?page=1&perPage=${totalItems}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      ),
      axiosHelper.get(`/qr/space/${spaceid}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      }),
    ]);

    const subspacesData = subSpaceRes?.data || [];
    const qrResponse = qrRes || [];

    return subspacesData
      .map((subspace) => {
        const matchingQR = qrResponse.find(
          (qr) => qr?.sub_space_id === subspace?.sub_space_id
        );
        return {
          ...subspace,
          qrKey: matchingQR?.key || null,
        };
      })
      .filter((subspace) => subspace.qrKey);
  };

  const downloadAllSubSpaceQRs = async () => {
    setIsDownloadingQR(true);

    try {
      const allSubSpaces = await fetchAllSubSpacesForDownload();

      if (allSubSpaces.length === 0) {
        alert(t("No subspaces with QR codes found"));
        return;
      }

      setAllDownloadSubSpaces(allSubSpaces);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const zip = new JSZip();
      const folder = zip.folder(String(spaceid));
      let successCount = 0;

      for (const subspace of allSubSpaces) {
        const qrElement = qrRefs.current[subspace.sub_space_id];
        if (!qrElement) continue;

        const blob = await convertQRToBlob(qrElement);
        if (blob) {
          const fileName = `${spaceid} - ${sanitizeFileName(subspace.name)}.png`;
          folder.file(fileName, blob);
          successCount++;
        }
      }

      if (successCount === 0) {
        alert(
          t(
            "Failed to generate any QR codes. The logo images may be blocked by CORS policy."
          )
        );
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `${spaceid}.zip`);

      if (successCount < allSubSpaces.length) {
        alert(
          t(
            `Successfully downloaded ${successCount} out of ${allSubSpaces.length} QR codes.`
          )
        );
      }
    } catch (error) {
      console.error("Error downloading QR codes:", error);
      alert(t("Failed to download QR codes. Please try again."));
    } finally {
      setAllDownloadSubSpaces([]);
      setIsDownloadingQR(false);
    }
  };

  // Show loading state while space info is loading
  if (isLoadingSpace) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading space information...</p>
        </div>
      </div>
    );
  }

  // Show error state if space loading failed
  if (isError && !space) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <div className="text-center">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error Loading Space</h4>
            <p>
              {error || "Failed to load space information. Please try again."}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state when no subspaces exist and we're not showing the modal
  if (
    !isLoadingSubspaces &&
    !isLoadingSpace &&
    !isError &&
    space &&
    subspaces.length === 0 &&
    !showAddSubSpaceModal
  ) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <div className="text-center">
          <div className="mb-4">
            <i
              className="ri-building-4-line"
              style={{ fontSize: "4rem", color: "#6c757d" }}
            ></i>
          </div>
          <h4>No Sub-Spaces Found</h4>
          <p className="text-muted mb-4">
            This space doesn't have any sub-spaces yet. Create your first
            sub-space to get started.
          </p>
          <button className="btn btn-primary" onClick={toggleAddSubSpaceModal}>
            <i className="ri-add-line me-2"></i>
            Create First Sub-Space
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <DataTableBase
        tableTitle={`${space?.name || "Loading..."}'s Sub-Spaces`}
        data={subspaces}
        columns={columns}
        loading={isLoadingSubspaces}
        paginationTotalRows={pagination?.totalItems || 0}
        paginationPerPage={rowsPerPage}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onHeaderAddBtnClick={toggleAddSubSpaceModal}
        onHeaderDeleteBtnClick={() => {
          alert("Soon");
        }}
        actionColWidth="100px"
        showSearch={false}
        showActionColumn={canEdit("space")}
        showSubHeader={canEdit("space")}
        showActionButtons={canEdit("space")}
        paginationLeftContent={
          <Button
            onClick={downloadAllSubSpaceQRs}
            disabled={
              isLoadingSubspaces || subspaces.length === 0 || isDownloadingQR
            }
            style={{ backgroundColor: "#c59253" }}
            className="btn-sm text-white"
          >
            {isDownloadingQR ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t("Downloading...")}
              </>
            ) : (
              <>
                <i className="ri-qr-code-line me-2"></i>
                {t("Download QR Codes")}
              </>
            )}
          </Button>
        }
        customActionBtns={(row) => (
          <>
            <button
              className="btn btn-sm btn-warning"
              onClick={() => {
                toggleEditSubSpace(row);
              }}
              title="Edit"
            >
              <i className="ri-edit-fill"></i>{" "}
            </button>

            <button
              className="btn btn-sm btn-danger"
              onClick={() => {
                showConfirm(
                  () => {
                    deleteSubSpaceFun(spaceid, row?.sub_space_id);
                  },
                  () => {
                    console.log("Cancelled");
                  },
                  "Confirmation",
                  `Are you sure you want to delete subSpace ${row?.name} (id:${row?.sub_space_id})?`,
                );
              }}
              title="Delete"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </>
        )}
      />

      {showAddSubSpaceModal && (
        <AddSubSpaces
          menuIDs={menuIDs}
          kitchenID={kitchenID}
          toggleAddSubSpaceModal={toggleAddSubSpaceModal}
          showAddSubSpaceModal={showAddSubSpaceModal}
          space_id={spaceid}
          onSubSpaceCreated={() => {
            // Refresh subspaces after creation
            fetchSubSpaces(currentPage, rowsPerPage);
          }}
        />
      )}

      {selectedRow && (
        <EditSubSpace
          toggleEditSubSpace={toggleEditSubSpace}
          showEditSubSpaceModal={showEditSubSpaceModal}
          rowData={selectedRow}
          space_id={spaceid}
        />
      )}

      {selectedRowQR && (
        <QRmodal
          info={selectedRowQR}
          altImage={space?.site?.image_url}
          showQRCode={showQRCode}
          toggleQRModal={() => setShowQRCode(!showQRCode)}
        />
      )}

      {allDownloadSubSpaces.length > 0 && (
        <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
          {allDownloadSubSpaces.map((subspace) => (
            <div
              key={`qr-download-${subspace.sub_space_id}`}
              ref={(el) => {
                qrRefs.current[subspace.sub_space_id] = el;
              }}
            >
              <QRCode
                value={`${process.env.REACT_APP_QR_URL}/menu/${subspace.qrKey}`}
                size={512}
                eyeRadius={10}
                ecLevel="H"
                logoPaddingStyle="circle"
                bgColor="transparent"
                logoImage={space?.site?.image_url}
                enableCORS={true}
                crossOrigin="anonymous"
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default SubSpaces;
